import { REQUEST_STATUS } from "../../constants/status";
import type {
  RequestDetailResponseDtoT,
  VacationRequestRecordDtoT,
} from "../../domain/dtos/vacationRequests.dto";
import type { VacationRequestEntityT } from "../../domain/entities/vacationRequest.entity";
import type { VacationRequestsMapperT } from "../types/vacationRequests.types";

const mapItemToDomain = (
  item: VacationRequestRecordDtoT | RequestDetailResponseDtoT,
): VacationRequestEntityT => ({
  id: item.request_id,
  requestNumber: item.request_number,
  collaborator: item.collaborator
    ? {
        id: item.collaborator.id,
        code: item.collaborator.code,
        fullName: item.collaborator.full_name,
        department: item.collaborator.department,
        avatar: item.collaborator.avatar,
        mobilePhone: item.collaborator.mobile_phone,
        phoneNumber: item.collaborator.phone_number,
        schedule: item.collaborator.schedule
          ? {
              code: item.collaborator.schedule.code,
              starts: item.collaborator.schedule.starts,
              ends: item.collaborator.schedule.ends,
              scheduleDisplay: item.collaborator.schedule.schedule_display,
            }
          : null,
      }
    : null,
  type: item.type
    ? {
        id: item.type.id,
        code: item.type.code,
        name: item.type.name,
        description: item.type.description,
        color: item.type.color,
        icon: item.type.icon,
      }
    : null,
  startDate: item.start_date,
  endDate: item.end_date,
  totalDays: item.total_days,
  reason: item.reason ?? null,
  status: REQUEST_STATUS[item.status] ?? REQUEST_STATUS.UNKNOWN,
  isAutoApproved: item.is_auto_approved ?? false,
  leader: item.leader
    ? {
        id: item.leader.id,
        code: item.leader.code,
        name: item.leader.full_name,
      }
    : null,
  createdDate: item.created_date,
  audit: item.audit
    ? {
        history: (item.audit.history ?? []).map((entry) => ({
          changedBy: entry.changed_by,
          changedByName: entry.changed_by_name,
          changedByEmail: entry.changed_by_email,
          changedByIsIdentity: entry.changed_by_is_identity,
          changedDate: entry.changed_date,
          oldStatus: REQUEST_STATUS[entry.old_status] ?? REQUEST_STATUS.UNKNOWN,
          newStatus: REQUEST_STATUS[entry.new_status] ?? REQUEST_STATUS.UNKNOWN,
          reason: entry.reason ?? null,
        })),
      }
    : null,
});

const mapFailedItemToDomain = (item: {
  request_id: string;
  reason: string;
}) => ({
  request_id: item.request_id,
  reason: item.reason,
});

const mapOverlapToDomain = (item) => ({
  avatar: item.avatar,
  fullName: item.full_name,
  requestId: item.request_id,
  typeCode: item.type_code,
  typeName: item.type_name,
  typeIcon: item.type_icon,
  status: item.status,
  startDate: item.start_date,
  endDate: item.end_date,
});

export const vacationRequestsMapper: VacationRequestsMapperT = {
  vacationRequestsDtoToDomain: (rawData) => {
    const data = rawData?.data?.items ?? [];

    if (!Array.isArray(data) || data.length === 0) {
      return [];
    }

    return data.map(mapItemToDomain);
  },
  requestDetailDtoToDomain: (rawData) => {
    const detail = rawData?.data;

    if (!detail) {
      throw new Error("No se recibió el detalle de la solicitud");
    }

    return {
      ...mapItemToDomain(detail),
      overlapping: (detail.overlapping ?? []).map(mapOverlapToDomain),
      balance: detail.balance
        ? {
            excludedDays: detail.balance.excluded_days,
            available: detail.balance.available,
            remaining: detail.balance.remaining,
          }
        : null,
    };
  },
  requestFilesDtoToDomain: (rawData) => {
    const data = rawData?.data;

    if (!data) {
      throw new Error(
        "Ocurrió un error al obtener los archivos de esta solicitud",
      );
    }

    return {
      expiresAt: data.expires_at,
      path: data.path,
      files: (data.files ?? []).map((file) => ({
        fileName: file.file_name,
        key: file.key,
        url: file.url,
        size: file.size,
      })),
    };
  },
  toUpdateDto: (domainData) => {
    return {
      items: domainData.map((item) => ({
        request_id: item.requestId,
        status: item.newStatus,
        comment: item.comment ?? "",
      })),
    };
  },
  updateVacationRequestDtoToDomain: (rawData) => {
    const result = rawData?.data;

    if (!result) {
      throw new Error("No se recibió la respuesta de actualización");
    }

    return {
      succeeded: (result.succeeded ?? []).map(mapItemToDomain),
      failed: (result.failed ?? []).map(mapFailedItemToDomain),
    };
  },
};
